import { clearAuthStorage, isTokenExpired } from '@utils/tokenUtils';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
  ? `${import.meta.env.VITE_API_BASE_URL}/api/v1`
  : 'http://localhost:3000/api/v1';

// API Error interface
export interface ApiError {
  message: string;
  status?: number;
  code?: string;
}

// Generic API response wrapper
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: ApiError;
}

// Helper to get token from sessionStorage
const getAuthToken = (): string | null => {
  return sessionStorage.getItem('authToken');
};

// Callback to handle token expiration
let onTokenExpired: (() => void) | null = null;

export const setTokenExpiredCallback = (callback: () => void): void => {
  onTokenExpired = callback;
};

// Store reference for dispatching Redux actions
let storeDispatch:
  | ((_action: { type: string; payload?: unknown }) => void)
  | null = null;

export const setStoreDispatch = (
  dispatch: (_action: { type: string; payload?: unknown }) => void,
): void => {
  storeDispatch = dispatch;
};

// Base API client
class ApiClient {
  private axiosInstance: AxiosInstance;
  private isRedirecting = false;

  constructor(baseURL: string) {
    this.axiosInstance = axios.create({
      baseURL,
      timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  // Setup request and response interceptors
  private setupInterceptors(): void {
    // Request interceptor: Add token and check expiration
    this.axiosInstance.interceptors.request.use(
      config => {
        const token = getAuthToken();
        const isPublicRoute = this.isPublicRoute(config);

        // Check token expiration using centralized helper
        if (
          !isPublicRoute &&
          isTokenExpired(sessionStorage.getItem('tokenExpiresAt'))
        ) {
          this.handleTokenExpiration();
          return Promise.reject(
            new Error('Session expired. Please validate again.'),
          );
        }

        // Add Authorization header if token exists
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      },
      error => {
        return Promise.reject(error);
      },
    );

    // Response interceptor: Handle 401 errors
    this.axiosInstance.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          this.handleTokenExpiration();
        }

        return Promise.reject(error);
      },
    );
  }

  // Check if route is public (doesn't require authentication)
  private isPublicRoute(config: AxiosRequestConfig): boolean {
    const isPostUsers =
      config.method?.toLowerCase() === 'post' && config.url === '/users';

    return isPostUsers;
  }

  // Centralized token expiration handling
  private handleTokenExpiration(): void {
    // Use centralized helper to clear storage
    clearAuthStorage();

    // Dispatch Redux action to clear auth state
    if (storeDispatch) {
      storeDispatch({ type: 'user/clearAuthToken' });
    }

    // Trigger expiration callback (redirect to validation)
    if (!this.isRedirecting && onTokenExpired) {
      this.isRedirecting = true;
      onTokenExpired();

      setTimeout(() => {
        this.isRedirecting = false;
      }, 1000);
    }

    // Dispatch custom event for other components to listen
    window.dispatchEvent(new Event('token:expired'));
  }

  // Generic request handler
  private async request<T>(
    config: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<T> =
        await this.axiosInstance.request(config);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // Handle session expiration
        if (
          error.response?.status === 401 ||
          error.message === 'Session expired. Please validate again.'
        ) {
          return {
            success: false,
            error: {
              message: 'Session expired. Please validate again.',
              status: 401,
              code: 'SESSION_EXPIRED',
            },
          };
        }

        // Handle other API errors
        const apiError: ApiError = {
          message:
            error.response?.data?.message ||
            error.message ||
            'An error occurred',
          status: error.response?.status,
          code: error.response?.data?.code || error.code,
        };

        return {
          success: false,
          error: apiError,
        };
      }

      // Handle unexpected errors
      return {
        success: false,
        error: {
          message: 'An unexpected error occurred',
          status: 0,
        },
      };
    }
  }

  // HTTP method helpers
  async get<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'GET', url });
  }

  async post<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'POST', url, data });
  }

  async put<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'PUT', url, data });
  }

  async patch<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'PATCH', url, data });
  }

  async delete<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'DELETE', url });
  }
}

// Create and export the API client instance
export const apiClient = new ApiClient(API_BASE_URL);

export default apiClient;
